<template>
  <main-template>
    <div class="rh-stepper">
      <div class="d-flex align-center flex-wrap gap-2 mb-2">
        <v-btn
          color="primary"
          icon
          large
          class="mr-2"
          :disabled="submittingForm"
          @click="sairDaPagina"
        >
          <v-icon>fa fa-angle-left</v-icon>
        </v-btn>
        <div class="min-width-0">
          <h1 class="text-h5 text-sm-h4 font-weight-black primary--text mb-0">Recursos Humanos</h1>
          <div class="text-body-2 grey--text text-truncate">
            {{ form.pessoal.name || "Colaborador" }}
          </div>
        </div>
        <v-spacer />
        <v-chip small outlined color="primary" class="font-weight-bold">
          <v-icon left small>{{ etapas[etapaAtual - 1].icone }}</v-icon>
          Etapa {{ etapaAtual }} de {{ etapas.length }}
        </v-chip>
      </div>

      <v-progress-linear
        :value="progresso"
        color="primary"
        height="6"
        rounded
        class="mt-2 mb-4 rh-progress"
      />

      <v-stepper v-model="etapaAtual" flat alt-labels non-linear class="rh-stepper__nav mb-6">
        <v-stepper-header class="rh-stepper__header">
          <template v-for="(etapa, index) in etapas">
            <v-stepper-step
              :key="`step-${etapa.id}`"
              :step="index + 1"
              :complete="etapaAtual > index + 1"
              :rules="[() => !etapasComErro.includes(index + 1)]"
              editable
              edit-icon="mdi-check"
            >
              <span class="rh-stepper__label">{{ etapa.titulo }}</span>
            </v-stepper-step>
            <v-divider v-if="index < etapas.length - 1" :key="`div-${etapa.id}`" />
          </template>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content :step="1" class="pa-0">
            <etapa-informacoes-pessoais
              ref="etapa-pessoal"
              :form="form"
              :lideres-list="lideresList"
              :pode-editar-foto="isAdminOrSuperAdmin"
              :uploading-foto="uploadingFoto"
              :documentos="documentosDaCategoria('pessoais')"
              @selecionar-foto="aoSelecionarFoto"
              v-on="eventosDocumento"
            />
          </v-stepper-content>

          <v-stepper-content :step="2" class="pa-0">
            <etapa-documentacao-contato
              ref="etapa-documentacao"
              :form="form"
              :documentos="documentosDaCategoria('documentacao')"
              v-on="eventosDocumento"
            />
          </v-stepper-content>

          <v-stepper-content :step="3" class="pa-0">
            <etapa-endereco
              :form="form"
              :documentos="documentosDaCategoria('endereco')"
              v-on="eventosDocumento"
            />
          </v-stepper-content>

          <v-stepper-content :step="4" class="pa-0">
            <etapa-contratos-vinculos
              :form="form"
              :documentos="documentosDaCategoria('contratos')"
              :cargos-db="cargosDb"
              :franquias-list="franquiasList"
              :carregando-franquias="carregandoFranquias"
              :is-super-admin="isSuperAdmin"
              @cargo-criado="aoCriarCargo"
              v-on="eventosDocumento"
            />
          </v-stepper-content>

          <v-stepper-content :step="5" class="pa-0">
            <etapa-formacao-academica
              :form="form"
              :documentos="documentosDaCategoria('formacao')"
              v-on="eventosDocumento"
            />
          </v-stepper-content>

          <v-stepper-content :step="6" class="pa-0">
            <etapa-anexos :form="form" :documentos="documentos" v-on="eventosDocumento" />
          </v-stepper-content>

          <v-stepper-content :step="7" class="pa-0">
            <etapa-escalas @carregadas="escalas = $event" />
          </v-stepper-content>

          <v-stepper-content :step="8" class="pa-0">
            <etapa-revisao
              :form="form"
              :documentos="documentos"
              :escalas="escalas"
              :lideres-list="lideresList"
              :alterado="isFormChanged"
              @ir-para="etapaAtual = $event"
            />
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <rh-etapa-nav
        :etapa-atual="etapaAtual"
        :total="etapas.length"
        :submitting="submittingForm"
        :pode-salvar="isFormChanged"
        dica="Campos com * são obrigatórios. Os arquivos são enviados ao salvar."
        @voltar="etapaAtual -= 1"
        @avancar="avancarEtapa"
        @salvar="submeterFormulario"
        @cancelar="sairDaPagina"
      />
    </div>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import {
  ETAPAS_RH,
  categoriaDoArquivo,
  nomeOriginalDoArquivo,
  normalizarDocumentos,
  novaFormacao,
  novoProfissional,
} from "./config/rhConfig";

import EtapaInformacoesPessoais from "./steps/EtapaInformacoesPessoais.vue";
import EtapaDocumentacaoContato from "./steps/EtapaDocumentacaoContato.vue";
import EtapaEndereco from "./steps/EtapaEndereco.vue";
import EtapaContratosVinculos from "./steps/EtapaContratosVinculos.vue";
import EtapaFormacaoAcademica from "./steps/EtapaFormacaoAcademica.vue";
import EtapaAnexos from "./steps/EtapaAnexos.vue";
import EtapaEscalas from "./steps/EtapaEscalas.vue";
import EtapaRevisao from "./steps/EtapaRevisao.vue";
import RhEtapaNav from "./components/RhEtapaNav.vue";

const LIMITE_DOCUMENTO_BYTES = 2 * 1024 * 1024;

export default {
  name: "RecursosHumanosStepperPage",
  components: {
    EtapaInformacoesPessoais,
    EtapaDocumentacaoContato,
    EtapaEndereco,
    EtapaContratosVinculos,
    EtapaFormacaoAcademica,
    EtapaAnexos,
    EtapaEscalas,
    EtapaRevisao,
    RhEtapaNav,
  },
  data() {
    return {
      etapas: ETAPAS_RH,
      etapaAtual: 1,
      etapasComErro: [],
      submittingForm: false,
      uploadingFoto: false,
      lideresList: [],
      franquiasList: [],
      carregandoFranquias: false,
      cargosDb: [],
      escalas: [],
      // Nomes como estão gravados no servidor; o download é feito pelo índice nesta lista.
      documentosServidor: [],
      documentosNovos: [],
      form: {
        pessoal: {
          name: "",
          user_id: null,
          rh_id: null,
          lider_id: null,
          estado_civil_id: null,
          sexo_id: null,
          cor_ou_raca: "",
          data_nascimento: null,
          cpf: "",
          rg: "",
          ctps: "",
          serie: "",
          email: "",
          telefone: "",
          uf_residencia: "",
          cep: "",
          municipio_residencia: "",
          bairro_residencia: "",
          rua_residencia: "",
          numero_residencia: "",
          complemento_residencia: "",
          area_do_curso: "",
          curso: "",
          nivel_escolaridade_concluido: "",
          documento: [],
          foto: null,
        },
        profissionais: [],
        formacoes: [],
      },
      originalFormJson: null,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    isAdminOrSuperAdmin() {
      return Boolean(this.user && ["admin", "super-admin"].includes(this.user.roleName));
    },
    isSuperAdmin() {
      if (this.user && this.user.roleName === "super-admin") {
        return true;
      }
      if (this.user && Array.isArray(this.user.roles)) {
        return this.user.roles.some((role) => role.name === "super-admin");
      }
      return false;
    },
    progresso() {
      return Math.round((this.etapaAtual / this.etapas.length) * 100);
    },
    documentos() {
      const salvos = this.form.pessoal.documento.map((arquivo) => {
        const indice = this.documentosServidor.indexOf(arquivo);
        return {
          id: `salvo-${arquivo}`,
          nome: nomeOriginalDoArquivo(arquivo) || `Documento ${indice + 1}`,
          arquivo,
          indice,
          categoria: categoriaDoArquivo(arquivo),
          novo: false,
        };
      });
      const novos = this.documentosNovos.map((d) => ({
        id: d.id,
        nome: d.file.name,
        categoria: d.categoria,
        tamanho: d.file.size,
        novo: true,
      }));
      return [...salvos, ...novos];
    },
    eventosDocumento() {
      return {
        "adicionar-documentos": this.aoAdicionarDocumentos,
        "remover-documento": this.aoRemoverDocumento,
        "baixar-documento": this.aoBaixarDocumento,
      };
    },
    isFormChanged() {
      if (!this.originalFormJson) {
        return false;
      }
      return this.documentosNovos.length > 0 || JSON.stringify(this.form) !== this.originalFormJson;
    },
  },
  watch: {
    etapaAtual() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    sairDaPagina() {
      this.$router.push({ name: "usuarios" });
    },
    documentosDaCategoria(categoria) {
      return this.documentos.filter((d) => d.categoria === categoria);
    },
    async validarEtapa(numero) {
      const componente = this.$refs[`etapa-${this.etapas[numero - 1].id}`];
      const valida = componente ? await componente.validar() : true;
      this.etapasComErro = valida
        ? this.etapasComErro.filter((n) => n !== numero)
        : [...new Set([...this.etapasComErro, numero])];
      return valida;
    },
    async avancarEtapa() {
      if (!(await this.validarEtapa(this.etapaAtual))) {
        this.$toast.danger("Existem campos inválidos nesta etapa. Revise antes de continuar.");
        return;
      }
      this.etapaAtual += 1;
    },
    async validarTodasEtapas() {
      const resultados = await Promise.all(this.etapas.map((e, i) => this.validarEtapa(i + 1)));
      const primeiraInvalida = resultados.indexOf(false) + 1;
      if (primeiraInvalida > 0) {
        this.etapaAtual = primeiraInvalida;
        this.$toast.danger(
          `Existem campos inválidos na etapa "${this.etapas[primeiraInvalida - 1].titulo}".`
        );
        return false;
      }
      return true;
    },
    aoAdicionarDocumentos({ files, categoria }) {
      let adicionados = 0;
      files.forEach((file) => {
        if (file.type !== "application/pdf") {
          this.$toast.danger(`Formato de arquivo inválido: ${file.name}. Envie apenas PDF.`);
          return;
        }
        if (file.size > LIMITE_DOCUMENTO_BYTES) {
          this.$toast.danger(`O arquivo ${file.name} excede o limite de 2MB.`);
          return;
        }
        this.documentosNovos.push({
          id: `novo-${Date.now()}-${this.documentosNovos.length}-${file.name}`,
          file,
          categoria: categoria || "geral",
        });
        adicionados += 1;
      });
      if (adicionados) {
        this.$toast.success(
          adicionados === 1 ? "Documento adicionado" : `${adicionados} documentos adicionados`
        );
      }
    },
    aoRemoverDocumento(item) {
      if (item.novo) {
        this.documentosNovos = this.documentosNovos.filter((d) => d.id !== item.id);
        return;
      }
      this.form.pessoal.documento = this.form.pessoal.documento.filter((a) => a !== item.arquivo);
    },
    async aoBaixarDocumento(item) {
      try {
        await this.$services.recursosHumanosService.downloadDocumento(
          this.$route.params.usuario_id,
          item.indice
        );
      } catch (error) {
        this.$handleError(error);
      }
    },
    aoCriarCargo(cargo) {
      const existe = this.cargosDb.some(
        (c) => String(c.nome).toLowerCase() === String(cargo.nome).toLowerCase()
      );
      if (!existe) {
        this.cargosDb.push(cargo);
      }
    },
    async aoSelecionarFoto(file) {
      if (!file.type.startsWith("image/")) {
        this.$toast.danger("Formato de arquivo inválido. Por favor, envie uma imagem.");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);

      this.uploadingFoto = true;
      try {
        const { usuario_id } = this.$route.params;
        const response = await this.$services.recursosHumanosService.uploadFoto(
          formData,
          usuario_id
        );
        this.$set(this.form.pessoal, "foto", response.foto);

        if (this.user && parseInt(usuario_id, 10) === this.user.id) {
          const updatedUser = { ...this.user };
          if (!updatedUser.recursos_humanos) {
            updatedUser.recursos_humanos = {};
          }
          updatedUser.recursos_humanos.foto = response.foto;
          this.$store.commit("Auth/setUser", updatedUser);
        }

        this.$toast.success("Foto atualizada com sucesso!");
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.uploadingFoto = false;
      }
    },
    async enviarDocumentosNovos(usuarioId) {
      const porCategoria = this.documentosNovos.reduce((grupos, item) => {
        // eslint-disable-next-line no-param-reassign
        grupos[item.categoria] = [...(grupos[item.categoria] || []), item.file];
        return grupos;
      }, {});

      // Um envio por categoria, para a API gravar o prefixo certo em cada arquivo.
      // eslint-disable-next-line no-restricted-syntax
      for (const [categoria, arquivos] of Object.entries(porCategoria)) {
        const formData = new FormData();
        formData.append("categoria", categoria);
        arquivos.forEach((file, index) => formData.append(`files[${index}]`, file));
        // eslint-disable-next-line no-await-in-loop
        await this.$services.recursosHumanosService.uploadDocumentos(formData, usuarioId);
      }
    },
    async submeterFormulario() {
      if (!(await this.validarTodasEtapas())) {
        return;
      }

      this.submittingForm = true;
      const { usuario_id } = this.$route.params;
      this.form.pessoal.user_id = parseInt(usuario_id, 10);
      const payload = {
        user_id: parseInt(usuario_id, 10),
        ...this.form.pessoal,
        profissionais: this.form.profissionais,
        formacoes: this.form.formacoes,
      };

      try {
        await this.$services.recursosHumanosService.criarRecursosHumanos(payload);
        if (this.documentosNovos.length > 0 && usuario_id) {
          await this.enviarDocumentosNovos(usuario_id);
        }
        this.$toast.success("Dados de Recursos Humanos salvos com sucesso");
        this.$router.push({ name: "usuarios" });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.submittingForm = false;
      }
    },
    async loadData() {
      try {
        this.carregandoFranquias = true;
        this.franquiasList = await this.$services.franquiasService.syncAll();
        try {
          this.lideresList = await this.$services.usuariosService.syncAll();
        } catch (e) {
          console.error("Erro ao carregar lista de usuários/líderes", e);
        }
        try {
          this.cargosDb = await this.$services.recursosHumanosService.getCargos();
        } catch (e) {
          console.error("Erro ao carregar lista de cargos", e);
        }

        const { usuario_id } = this.$route.params;
        const dadosUsuario = await this.$services.recursosHumanosService.getUsuario(usuario_id);
        const dadosRH =
          (await this.$services.recursosHumanosService.getUsuarioRecursosHumanos(usuario_id)) || {};

        this.documentosServidor = normalizarDocumentos(dadosRH.documento);

        this.form.pessoal = {
          name: dadosUsuario.name,
          user_id: dadosRH.user_id,
          rh_id: dadosRH.id,
          lider_id: dadosRH.lider_id ? Number(dadosRH.lider_id) : null,
          estado_civil_id: dadosRH.estado_civil_id ? Number(dadosRH.estado_civil_id) : null,
          sexo_id: dadosRH.sexo_id ? Number(dadosRH.sexo_id) : null,
          cor_ou_raca: dadosRH.cor_ou_raca,
          data_nascimento: dadosRH.data_nascimento,
          cpf: dadosRH.cpf,
          rg: dadosRH.rg,
          ctps: dadosRH.ctps || "",
          serie: dadosRH.serie || "",
          email: dadosRH.email,
          telefone: dadosRH.telefone,
          uf_residencia: dadosRH.uf_residencia,
          cep: dadosRH.cep,
          municipio_residencia: dadosRH.municipio_residencia,
          bairro_residencia: dadosRH.bairro_residencia,
          rua_residencia: dadosRH.rua_residencia,
          numero_residencia: dadosRH.numero_residencia,
          complemento_residencia: dadosRH.complemento_residencia,
          area_do_curso: dadosRH.area_do_curso,
          curso: dadosRH.curso,
          nivel_escolaridade_concluido: dadosRH.nivel_escolaridade_concluido,
          documento: [...this.documentosServidor],
          foto: dadosRH.foto,
        };

        this.form.profissionais = (dadosRH.profissionais || []).map((prof) => ({
          matricula: prof.matricula,
          regime_trabalhista: prof.regime_trabalhista,
          cargo_concurso: prof.cargo_concurso,
          cargo_exercicio: prof.cargo_exercicio,
          franquias_permitidas: prof.franquias_permitidas
            ? JSON.parse(prof.franquias_permitidas)
            : [],
          data_ingresso: prof.data_ingresso,
          ch_total: prof.ch_total,
          ctps: prof.ctps || "",
          serie: prof.serie || "",
          turno: prof.turno ? JSON.parse(prof.turno) : [],
          id: prof.id,
        }));
        if (this.form.profissionais.length === 0) {
          this.form.profissionais.push(novoProfissional());
        }

        this.form.formacoes = (dadosRH.formacoes || []).map((formacao) => ({
          instituicao: formacao.instituicao,
          area_do_curso: formacao.area_do_curso,
          curso: formacao.curso,
          ano_conclusao: formacao.ano_conclusao,
          id: formacao.id,
        }));
        if (this.form.formacoes.length === 0) {
          this.form.formacoes.push(novaFormacao());
        }

        this.$loaderService.close();
        this.originalFormJson = JSON.stringify(this.form);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoFranquias = false;
      }
    },
  },
};
</script>

<style>
.rh-stepper__nav.v-stepper {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.rh-stepper__header {
  background: var(--surface) !important;
  border: 1px solid var(--border) !important;
  border-radius: 18px !important;
  box-shadow: 0 4px 16px var(--shadow) !important;
  align-items: center;
  height: auto !important;
  min-height: 92px;
  padding: 8px 4px;
  overflow-x: auto;
  flex-wrap: nowrap !important;
}

.rh-stepper__header .v-stepper__step {
  padding: 16px 12px;
  min-width: 110px;
}

.rh-stepper__header .v-divider {
  border-color: var(--border) !important;
  margin: 0 -8px !important;
}

.rh-stepper__header .v-stepper__label {
  text-align: center;
  display: block !important;
}

.rh-stepper__label {
  display: block;
  text-align: center;
  color: var(--text-muted) !important;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.3;
}

.rh-stepper .v-stepper__step--active .rh-stepper__label,
.rh-stepper .v-stepper__step--complete .rh-stepper__label {
  color: var(--brand) !important;
}

.rh-stepper .v-stepper__step--error .rh-stepper__label {
  color: var(--v-error-base) !important;
}

.rh-stepper .v-stepper__step__step {
  font-size: 15px !important;
  font-weight: 700 !important;
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  background-color: var(--surface-2) !important;
  border: 2px solid var(--border) !important;
  color: var(--text-muted) !important;
}

.rh-stepper .v-stepper__step--active .v-stepper__step__step,
.rh-stepper .v-stepper__step--complete .v-stepper__step__step {
  background-color: var(--brand) !important;
  border-color: var(--brand) !important;
  color: var(--brand-contrast) !important;
}

.rh-stepper .v-stepper__step--complete .v-stepper__step__step .v-icon {
  color: var(--brand-contrast) !important;
}

.rh-stepper .v-stepper__step--editable:hover {
  background-color: var(--brand-soft) !important;
  border-radius: 12px;
}

.rh-stepper .v-stepper__content {
  padding: 24px 0 16px !important;
}

.rh-progress {
  background-color: var(--surface-2) !important;
}

.rh-stepper .gap-2 {
  gap: 12px;
}

/* Mobile: só os círculos, todos visíveis sem rolagem; o título da etapa fica no card. */
@media (max-width: 599px) {
  .rh-stepper__header {
    min-height: 0;
    padding: 4px;
    overflow-x: visible;
    border-radius: 14px !important;
  }

  .rh-stepper__header .v-stepper__step {
    flex: 1 1 0;
    min-width: 0;
    padding: 10px 0;
  }

  .rh-stepper__header .v-stepper__label,
  .rh-stepper__header .v-divider {
    display: none !important;
  }

  .rh-stepper .v-stepper__step__step {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    font-size: 13px !important;
    margin-bottom: 0 !important;
  }

  .rh-stepper .v-stepper__content {
    padding-top: 16px !important;
  }
}
</style>
